export class DOMObserverService {
  private static observers: Map<string, MutationObserver> = new Map();

  /**
   * Creates an observer that watches for a specific class being added to an element
   * @param selector - The CSS selector for the element to watch
   * @param classToWatch - The class name to watch for
   * @param callback - The callback function to execute when the class is added
   * @returns A cleanup function to disconnect the observer
   */
  public static watchForClass(
    selector: string,
    classToWatch: string,
    callback: () => void
  ): () => void {
    const element = document.querySelector(selector);
    if (!element) {
      console.warn(`Element with selector "${selector}" not found`);
      return () => {};
    }

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "class"
        ) {
          const target = mutation.target as HTMLElement;
          if (target.classList.contains(classToWatch)) {
            callback();
          }
        }
      });
    });

    observer.observe(element, {
      attributes: true,
      attributeFilter: ["class"],
    });

    // Store the observer with a unique key
    const observerId = `${selector}-${classToWatch}`;
    this.observers.set(observerId, observer);

    // Return cleanup function
    return () => {
      observer.disconnect();
      this.observers.delete(observerId);
    };
  }

  /**
   * Disconnects all active observers
   */
  public static disconnectAll(): void {
    this.observers.forEach((observer) => {
      observer.disconnect();
    });
    this.observers.clear();
  }
}
